import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ButtonDarkMode from 'components/ButtonDarkMode/ButtonDarkMode';
import { HOME_SECTION_NAVIGATION_EVENT } from 'utils/homeSectionNavigation';
import './style.css';

const sectionLinks = [
  { id: 'services', label: 'Services' },
  { id: 'about', label: 'About' },
  { id: 'process', label: 'Process' },
];

const observedSectionIds = sectionLinks.map(({ id }) => id);

const Navbar = () => {
  const { hash, pathname } = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const menuButtonRef = useRef(null);

  const closeMenu = () => setIsMenuOpen(false);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [hash, pathname]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const pageElements = Array.from(document.querySelectorAll('main, footer'));
    const previousInertStates = pageElements.map(element => element.inert);

    document.body.style.overflow = 'hidden';
    pageElements.forEach(element => {
      element.inert = true;
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      pageElements.forEach((element, index) => {
        element.inert = previousInertStates[index];
      });
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const handleEscape = event => {
      if (event.key !== 'Escape') return;

      setIsMenuOpen(false);
      menuButtonRef.current?.focus();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isMenuOpen]);

  useEffect(() => {
    const desktopQuery = window.matchMedia('(min-width: 901px)');
    const handleDesktopChange = event => {
      if (event.matches) setIsMenuOpen(false);
    };

    desktopQuery.addEventListener('change', handleDesktopChange);
    return () => desktopQuery.removeEventListener('change', handleDesktopChange);
  }, []);

  useEffect(() => {
    if (pathname !== '/') {
      setActiveSection(null);
      return undefined;
    }

    const hashSection = hash.slice(1);
    setActiveSection(
      observedSectionIds.includes(hashSection) ? hashSection : null
    );
    return undefined;
  }, [hash, pathname]);

  useEffect(() => {
    if (pathname !== '/') return undefined;
    if (!('IntersectionObserver' in window)) return undefined;

    const sections = observedSectionIds
      .map(id => document.getElementById(id))
      .filter(Boolean);

    if (!sections.length) return undefined;

    const visibility = new Map();
    const navHeight =
      document.querySelector('.nav')?.getBoundingClientRect().height || 74;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          visibility.set(entry.target.id, entry.isIntersecting);
        });

        const visibleSections = sections
          .filter(section => visibility.get(section.id))
          .sort(
            (first, second) =>
              Math.abs(first.getBoundingClientRect().top - navHeight) -
              Math.abs(second.getBoundingClientRect().top - navHeight)
          );

        setActiveSection(visibleSections[0]?.id || null);
      },
      {
        rootMargin: `-${Math.ceil(navHeight)}px 0px -55% 0px`,
        threshold: [0, 0.01, 0.1, 0.25],
      }
    );

    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, [pathname]);

  const handleLogoClick = event => {
    closeMenu();

    if (pathname !== '/') return;

    event.preventDefault();

    if (hash) {
      navigate('/', {
        replace: true,
        state: { smoothScrollTop: true },
      });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSectionClick = (event, sectionId) => {
    closeMenu();
    const isCurrentSection =
      pathname === '/' && hash === `#${sectionId}`;

    window.dispatchEvent(
      new CustomEvent(HOME_SECTION_NAVIGATION_EVENT, {
        detail: {
          sectionId,
          scrollAfterOpen: isCurrentSection,
        },
      })
    );

    if (!isCurrentSection) return;

    event.preventDefault();

    if (window.matchMedia('(max-width: 767px)').matches) return;

    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const projectsActive =
    pathname === '/projects' || pathname.startsWith('/project/');

  return (
    <nav className="nav" aria-label="Main navigation">
      <div className="container">
        <div className="nav-row">
          <Link to="/" className="logo" onClick={handleLogoClick}>
            DotPolyline
          </Link>

          <button
            ref={menuButtonRef}
            type="button"
            className={`nav-menu-toggle${isMenuOpen ? ' is-open' : ''}`}
            aria-label={isMenuOpen ? 'Close main menu' : 'Open main menu'}
            aria-expanded={isMenuOpen}
            aria-controls="primary-navigation"
            onClick={() => setIsMenuOpen(current => !current)}
            onKeyDown={event => {
              if (event.key !== 'Enter' && event.key !== ' ') return;

              event.preventDefault();
              setIsMenuOpen(current => !current);
            }}
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>

          <div
            className={`nav-backdrop${isMenuOpen ? ' is-open' : ''}`}
            aria-hidden="true"
            onClick={closeMenu}
          />

          <div
            className={`nav-menu${isMenuOpen ? ' is-open' : ''}`}
            id="primary-navigation"
          >
            <ul className="nav-list">
              <li className="nav-list__item">
                <Link
                  to="/#services"
                  className={`nav-list__link${
                    pathname === '/' && activeSection === 'services'
                      ? ' active'
                      : ''
                  }`}
                  aria-current={
                    pathname === '/' && activeSection === 'services'
                      ? 'location'
                      : undefined
                  }
                  onClick={event => handleSectionClick(event, 'services')}
                >
                  Services
                </Link>
              </li>

              <li className="nav-list__item">
                <Link
                  to="/projects"
                  className={`nav-list__link${
                    projectsActive ? ' active' : ''
                  }`}
                  aria-current={projectsActive ? 'page' : undefined}
                  onClick={closeMenu}
                >
                  Projects
                </Link>
              </li>

              {sectionLinks.slice(1).map(({ id, label }) => {
                const isActive = pathname === '/' && activeSection === id;

                return (
                  <li className="nav-list__item" key={id}>
                    <Link
                      to={`/#${id}`}
                      className={`nav-list__link${
                        isActive ? ' active' : ''
                      }`}
                      aria-current={isActive ? 'location' : undefined}
                      onClick={event => handleSectionClick(event, id)}
                    >
                      {label}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <Link
              to="/contacts"
              className="nav-quote"
              aria-current={pathname === '/contacts' ? 'page' : undefined}
              onClick={closeMenu}
            >
              Request a Quote
            </Link>

            <div className="nav-theme">
              <span className="nav-theme__label">Theme</span>
              <ButtonDarkMode />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
